import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { Settings as SettingsIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import BoardCardModal from "../../components/BoardCardModal";
import CommercialDashboard from "../../components/CommercialDashboard";
import CompanySearchPanel from "../../components/CompanySearchPanel";
import CardFieldInput from "../../components/CardFieldInput";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// MainContainer corta (overflowY: hidden) qualquer conteudo mais alto
// que a tela em vez de rolar - sem isso aqui, o Painel (KPIs + funil +
// rosca + tabela de prospects) ficava cortado fora da tela sempre que
// nao cabia inteiro, e a unica forma de ver tudo era diminuir o zoom do
// navegador (bug real reportado pelo usuario 2026-09-16). Mesmo padrao
// (flex:1 + overflowY:scroll + scrollbarStyles) ja usado em outras
// telas do sistema (ex: PendingConfirmation) que tambem tem conteudo
// que pode passar da altura da tela.
const useStyles = makeStyles((theme) => ({
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    // Fix 2026-10-22: a spec de CSS promove overflow-x "visible" pra
    // "auto" automaticamente quando overflow-y nao e' "visible" - sem
    // isso aqui, qualquer overflow horizontal minimo (um pixel que seja,
    // de layout/grid) virava uma barra de rolagem horizontal inesperada
    // no meio da tela (achado no layout Novo, onde a area de conteudo
    // ficou mais larga e expos esse overflow que antes nao existia).
    overflowX: "hidden",
    minHeight: 0,
    ...theme.scrollbarStyles,
  },
}));

// Tela unica "Comercial" - junta o que antes eram 2 lugares separados
// (aba "Painel" dentro do Kanban + pagina "/company-search") numa so,
// pedido do usuario 2026-09-16 ("no kanban comercial onde esta o
// painel, isso deveria estar junto da tela de prospeccao e isso
// alimentar o kanban"). O Kanban (/boards) voltou a mostrar so' o
// quadro de arrastar - essa tela e' quem alimenta ele (tanto pelo
// "Novo prospect" avulso quanto pela busca CNPJa).
const Commercial = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [phases, setPhases] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const [addProspectOpen, setAddProspectOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPhaseId, setNewPhaseId] = useState("");
  const [newFieldValues, setNewFieldValues] = useState({});
  const [savingProspect, setSavingProspect] = useState(false);

  // Controle "modular" pedido pelo usuario 2026-09-16 ("e' necessario
  // deixar modular pra escolher o que eu quero pesquisar... cada funcao
  // consome uma quantidade [de credito]") - por enquanto so' a
  // geolocalizacao paga (unico extra da CNPJa usado ate' agora alem da
  // busca em si), desligada por padrao. Reaproveita o mecanismo
  // generico de CompaniesSettings ja corrigido (era vulneravel a SQL
  // injection - ver UpdateCompanySettingService).
  const [allowPaidGeocoding, setAllowPaidGeocoding] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api
      .get("/companySettingOne", { params: { column: "cnpjaAllowPaidGeocoding" } })
      .then(({ data }) => setAllowPaidGeocoding(data?.cnpjaAllowPaidGeocoding === "enabled"))
      .catch(() => {});
  }, []);

  const handleToggleGeocoding = async (checked) => {
    setSavingSettings(true);
    try {
      await api.put("/companySettings/", {
        column: "cnpjaAllowPaidGeocoding",
        data: checked ? "enabled" : "disabled",
      });
      setAllowPaidGeocoding(checked);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    api
      .get("/boards")
      .then(({ data }) => {
        setBoards(data);
        if (data.length > 0) setBoardId(data[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  const fetchBoardDetail = useCallback(async () => {
    if (!boardId) return;
    try {
      const { data } = await api.get(`/boards/${boardId}`);
      setPhases(data.phases || []);
      setCustomFieldDefinitions(data.customFieldDefinitions || []);
    } catch (err) {
      toastError(err);
    }
  }, [boardId]);

  useEffect(() => {
    fetchBoardDetail();
  }, [fetchBoardDetail]);

  const firstPhaseId = phases.find((p) => !p.isPendingColumn)?.id || null;

  const handleOpenCard = (card) => {
    setSelectedCard(card);
    setCardModalOpen(true);
  };

  const handleCardSaved = () => {
    setRefreshKey((k) => k + 1);
    fetchBoardDetail();
  };

  // Atalho pedido pelo usuario 2026-09-16 ("quando eu adicionar algo pro
  // kanban ter um atalho que me redirecione") - toast com link em vez de
  // redirecionar direto, porque o usuario normalmente adiciona varios
  // prospects em sequencia sem querer sair da tela a cada um.
  // /boards?board=X e' o formato que Boards/index.js ja sabe ler
  // (deep-link por query string, nao por path param).
  const showAddedToKanbanToast = (contactResult) => {
    let text = "Adicionado ao Kanban!";
    if (contactResult?.created) text = "Adicionado ao Kanban e cliente cadastrado!";
    else if (contactResult?.reason === "not-whatsapp") {
      text = "Adicionado ao Kanban - número não é WhatsApp, cadastre o contato manualmente se for o caso.";
    }
    toast.success(
      <span>
        {text}{" "}
        <span
          style={{ textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}
          onClick={() => history.push(`/boards?board=${boardId}`)}
        >
          Ver no Kanban
        </span>
      </span>
    );
  };

  // Mesma logica do CompanySearchPanel (ver tryCreateContact la') - aqui
  // o telefone vem de texto livre digitado no campo "Telefone" do
  // formulario "Novo prospect", entao precisa limpar formatacao antes.
  // Pedido do usuario 2026-09-16 ("preciso que as prospeccoes precise
  // cadastrar o cliente pois assim consigo enviar mensagem").
  const sanitizePhoneForContact = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) return null;
    return digits.startsWith("55") && digits.length >= 12 ? digits : `55${digits}`;
  };

  const tryCreateContact = async (name, phoneDigits) => {
    if (!phoneDigits) return { created: false, reason: "no-phone" };
    try {
      const { data } = await api.post("/contacts", { name, number: phoneDigits });
      return { created: true, contactId: data?.id };
    } catch (err) {
      const reason = err?.response?.data?.error?.includes("whatsapp") ? "not-whatsapp" : "unknown";
      return { created: false, reason };
    }
  };

  const resetAddProspectForm = () => {
    setAddProspectOpen(false);
    setNewTitle("");
    setNewPhaseId("");
    setNewFieldValues({});
  };

  const handleOpenAddProspect = () => {
    setNewPhaseId(firstPhaseId || "");
    setAddProspectOpen(true);
  };

  const handleNewFieldChange = (defId, value) => {
    setNewFieldValues((prev) => ({ ...prev, [defId]: value }));
  };

  const handleSaveProspect = async () => {
    if (savingProspect || !newTitle.trim() || !newPhaseId) return;
    setSavingProspect(true);
    try {
      const { data: card } = await api.post("/cards", {
        boardId,
        phaseId: newPhaseId,
        title: newTitle.trim(),
      });

      const fieldsForPhase = customFieldDefinitions.filter(
        (def) => !def.phaseId || def.phaseId === Number(newPhaseId)
      );
      const filledFields = fieldsForPhase.filter((def) => newFieldValues[def.id]);
      if (filledFields.length > 0) {
        await Promise.all(
          filledFields.map((def) =>
            api.post("/card-field-values", {
              cardId: card.id,
              customFieldDefinitionId: def.id,
              value: newFieldValues[def.id],
            })
          )
        );
      }

      const telefoneField = customFieldDefinitions.find((def) => def.name.toLowerCase() === "telefone");
      const phoneRaw = telefoneField ? newFieldValues[telefoneField.id] : null;
      const contactResult = await tryCreateContact(newTitle.trim(), sanitizePhoneForContact(phoneRaw));

      if (contactResult.created && contactResult.contactId) {
        await api.put(`/cards/${card.id}`, { contactId: contactResult.contactId });
      }

      showAddedToKanbanToast(contactResult);
      resetAddProspectForm();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingProspect(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Comercial</Title>
        <MainHeaderButtonsWrapper>
          <Select
            value={boardId}
            onChange={(e) => setBoardId(e.target.value)}
            variant="outlined"
            displayEmpty
          >
            {boards.length === 0 && (
              <MenuItem value="">
                <em>Nenhum board</em>
              </MenuItem>
            )}
            {boards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
          {user?.profile === "admin" && (
            <Tooltip title="Configurar custos (CNPJá)">
              <IconButton onClick={() => setSettingsOpen(true)}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box className={classes.scrollArea} style={{ width: "100%" }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab label="Painel" />
          <Tab label="Buscar empresas" />
        </Tabs>

        {boardId && tab === 0 && (
          <CommercialDashboard
            key={refreshKey}
            boardId={boardId}
            phases={phases}
            users={users}
            onOpenCard={handleOpenCard}
            onAddProspect={handleOpenAddProspect}
            onGoToSearch={() => setTab(1)}
          />
        )}

        {boardId && tab === 1 && (
          <CompanySearchPanel
            boardId={boardId}
            firstPhaseId={firstPhaseId}
            customFieldDefinitions={customFieldDefinitions}
            allowPaidGeocoding={allowPaidGeocoding}
            onAdded={(contactResult) => {
              setRefreshKey((k) => k + 1);
              showAddedToKanbanToast(contactResult);
            }}
          />
        )}
      </Box>

      <BoardCardModal
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        card={selectedCard}
        customFieldDefinitions={customFieldDefinitions}
        phases={phases}
        onSaved={handleCardSaved}
      />

      <Dialog open={addProspectOpen} onClose={resetAddProspectForm} maxWidth="xs" fullWidth>
        <DialogTitle>Novo prospect</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Título"
            fullWidth
            margin="dense"
            variant="outlined"
            autoFocus
            placeholder="Ex: Lead da feira, Indicação da Maria..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <TextField
            label="Fase"
            select
            fullWidth
            margin="dense"
            variant="outlined"
            value={newPhaseId}
            onChange={(e) => {
              setNewPhaseId(e.target.value);
              setNewFieldValues({});
            }}
          >
            {phases.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          {newPhaseId &&
            customFieldDefinitions
              .filter((def) => !def.phaseId || def.phaseId === Number(newPhaseId))
              .map((def) => (
                <CardFieldInput
                  key={def.id}
                  def={def}
                  value={newFieldValues[def.id]}
                  onChange={(value) => handleNewFieldChange(def.id, value)}
                />
              ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddProspectForm} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveProspect}
            color="primary"
            variant="contained"
            disabled={savingProspect || !newTitle.trim() || !newPhaseId}
          >
            {savingProspect ? "Adicionando..." : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Configurar custos (CNPJá)</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={allowPaidGeocoding}
                onChange={(e) => handleToggleGeocoding(e.target.checked)}
                disabled={savingSettings}
                color="primary"
              />
            }
            label="Geolocalização exata (CNPJá)"
          />
          <Typography variant="body2" color="textSecondary">
            Quando ligado, aparece um botão extra pra geolocalizar um lead com o endereço oficial da Receita Federal
            (mais preciso que o link gratuito do Google Maps) — cada uso consome 1 crédito do seu plano CNPJá. A busca
            de empresas e o link gratuito de mapa continuam funcionando normalmente independente desse toggle.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Commercial;
